import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subscription, type Observable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import {
  CrawlRunMoversService,
  type CrawlRunMoversPayload,
  type CrawlRunSiteMetricCard,
  type CrawlRunSiteSummary
} from '../../services/crawl-run-movers.service';
import { ImpactPackagesComponent } from '../impact-packages/impact-packages';
import {
  ImpactBriefService,
  type ImpactBriefPayload
} from '../../services/impact-brief.service';
import {
  impactBriefFirstRunPanel,
  type ImpactBriefFirstRunPanel
} from '../../utils/impact-brief-first-run';
import { impactBriefEmptyMessage } from '../../utils/impact-package-display';
import {
  buildCrawlRunPeriodView,
  type CrawlRunPeriodView
} from '../../services/crawl-run-period.service';
import {
  TechnicalRollupService,
  type SiteWideTechnicalIssue,
  type TechnicalRollup
} from '../../services/technical-rollup.service';
import { SiteWideIssuesComponent } from '../site-wide-issues/site-wide-issues';
import { InventoryPlanClustersService } from '../../services/inventory-plan-clusters.service';
import { DomainContextService } from '../../services/domain-context';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, ImpactPackagesComponent, SiteWideIssuesComponent],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css'
})
export class DashboardComponent implements OnInit, OnDestroy {
  loading = true;
  error: string | null = null;
  movers: CrawlRunMoversPayload | null = null;
  /**
   * This period's work packages (IMPACT_BRIEF_PLAN.md). Composed entirely
   * server-side and rendered in order — the dashboard picks nothing.
   */
  brief: ImpactBriefPayload | null = null;
  briefLoading = true;

  /**
   * Technical issues the template put on every page — the "fix once" list.
   *
   * Its own payload rather than a field on the brief: the brief is
   * period-scoped and a template defect has no period, so folding it in would
   * make this section wait on a crawl pair it does not need.
   */
  technicalRollupPayload: TechnicalRollup | null = null;
  technicalRollupLoading = true;

  /**
   * Packages the operator has opened past the five-page cap.
   *
   * Accumulated rather than replaced: opening a second package must not fold
   * the first one back up. Sent on every subsequent reload so the server
   * returns all of them uncapped.
   */
  expandedPackageIds: string[] = [];
  /** The one being fetched right now, for the row's own loading state. */
  expandingPackageId: string | null = null;

  constructor(
    private moversService: CrawlRunMoversService,
    private impactBriefService: ImpactBriefService,
    private technicalRollupService: TechnicalRollupService,
    private inventoryPlanClusters: InventoryPlanClustersService,
    private domainContext: DomainContextService
  ) {}

  private readonly subscriptions = new Subscription();
  /** The domain the page last loaded for; `undefined` until the first load. */
  private loadedDomain: string | null | undefined = undefined;

  /**
   * Fetch the page signals before anyone opens a row.
   *
   * All three are domain-scoped, so the answer is the same whichever row is
   * opened first — and the peek cannot even start them until its own analysis
   * call returns, which is why the Internal links row used to arrive a beat
   * after the rest of the card and push the layout down.
   *
   * Fired once the domain is known and left to run in the background: nothing
   * on this screen waits for it, and by the time an operator has read the strip
   * and clicked a row the service has the answer in hand. Errors are ignored on
   * purpose — a warm-up that fails costs the peek nothing it did not already
   * have, and must never surface an error on a screen that did not ask.
   */
  private warmPageSignals(domain: string | null): void {
    const scope = (domain || '').trim();
    if (!scope || this.warmedSignalDomains.has(scope)) return;
    this.warmedSignalDomains.add(scope);

    const opts = { includeCompleted: false, includeDismissed: false };
    // The same arguments the peek uses. A different `limit` is a different
    // cache key, so a mismatch here would warm something nothing reads.
    this.inventoryPlanClusters
      .getCannibalizationActionPlan(scope, { limit: 200, includePageIntersection: false, ...opts })
      .subscribe({ next: () => {}, error: () => {} });
    this.inventoryPlanClusters
      .getUnclusteredPageActionPlan(scope, { limit: 200, ...opts })
      .subscribe({ next: () => {}, error: () => {} });
  }

  /** Domains already warmed this session, so a reload does not refetch. */
  private readonly warmedSignalDomains = new Set<string>();

  /**
   * The page shows the domain selected in the header, and reloads when it changes.
   *
   * It used to ask the server for "the dashboard" and let it pick the workspace's
   * newest crawl pair — which, once competitors had two captured runs, was a
   * competitor's: after the 2026-10-04 recrawl it titled itself assembly.com
   * while the header said manyrequests.com (TECHNICAL_DEBT §0bk). The server
   * pick remains only for an account with no domain to select yet.
   */
  ngOnInit() {
    this.domainContext.ensureInitialized();
    this.subscriptions.add(
      combineLatest([this.domainContext.loaded$, this.domainContext.selectedDomain$]).subscribe(
        ([loaded, selected]) => {
          if (!loaded) return;
          const domain = selected || null;
          if (domain === this.loadedDomain) return;
          this.loadedDomain = domain;
          this.expandedPackageIds = [];
          this.load(domain);
        }
      )
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  load(domain: string | null = this.loadedDomain ?? null) {
    this.loading = true;
    this.error = null;
    this.briefLoading = true;
    this.brief = null;

    const movers$: Observable<{ success: boolean; data: CrawlRunMoversPayload | null }> = domain
      ? this.moversService.getDomainMovers(domain, { limit: 5 })
      : this.moversService.getDashboardMovers(5);

    movers$.subscribe({
      next: (res) => {
        this.movers = res.data;
        this.loading = false;
        const scope = domain ?? res.data?.scopeDomain ?? null;
        this.loadBrief(scope);
        this.loadTechnicalRollup(scope);
        this.warmPageSignals(scope);
      },
      error: (err) => {
        console.error('Failed to load dashboard movers:', err);
        this.error = 'Could not load crawl movers. Is the backend running?';
        this.loading = false;
        // The movers are not a prerequisite for having a week plan: keep the
        // packages on the selected domain rather than dropping them too.
        this.loadBrief(domain);
        this.loadTechnicalRollup(domain);
      }
    });
  }

  /**
   * The site-wide technical section, pinned to the same domain as the packages.
   *
   * Independent of the brief on purpose. It reads `PageAnalysis` rather than the
   * synthesis, so it renders on a first-run domain with no pair, and a brief
   * that fails does not take it down.
   */
  private loadTechnicalRollup(domain: string | null) {
    this.technicalRollupLoading = true;

    this.technicalRollupService.getRollup(domain).subscribe({
      next: (payload) => {
        this.technicalRollupPayload = payload;
        this.technicalRollupLoading = false;
      },
      error: () => {
        this.technicalRollupPayload = null;
        this.technicalRollupLoading = false;
      }
    });
  }

  /**
   * Whether the section has anything to show.
   *
   * A rollup that loaded and found nothing site-wide renders nothing at all,
   * rather than an empty panel congratulating the operator: on a healthy site
   * this section's absence IS the good news, and a permanent "no site-wide
   * issues" card is furniture on every load thereafter.
   */
  get hasSiteWideIssues(): boolean {
    return (this.technicalRollupPayload?.siteWide.length ?? 0) > 0;
  }

  get siteWideIssues(): SiteWideTechnicalIssue[] {
    return this.technicalRollupPayload?.siteWide ?? [];
  }

  /**
   * Sequenced after the movers so the brief can be pinned to `scopeDomain` — the
   * domain this page just named in its header. The server would resolve the same
   * domain from an omitted parameter today, but pinning it means the packages
   * describe the site on screen by construction rather than by two code paths
   * agreeing, which matters in a multi-domain workspace.
   *
   * Not gated on the movers *succeeding*, though: the two answer different
   * questions — packages are what to do, movers are the evidence — and neither
   * should blank the other. The service swallows a failure into null, so a brief
   * that cannot load costs the operator this section and nothing else.
   */
  private loadBrief(domain: string | null) {
    this.briefLoading = true;

    this.impactBriefService
      .getBrief(domain, { expandedPackageIds: this.expandedPackageIds })
      .subscribe({
        next: (payload) => {
          this.brief = payload;
          this.briefLoading = false;
        },
        error: () => {
          this.brief = null;
          this.briefLoading = false;
        }
      });
  }

  /**
   * "+13 more pages in this package".
   *
   * Refetches the brief with this package uncapped rather than asking for the
   * missing rows on their own. The pages come from the same synthesis in the
   * same order, so the five already on screen stay put and stay first — a
   * separate "give me the tail" call would have to re-derive that order and
   * could disagree with the head the operator is already reading.
   *
   * `briefLoading` is deliberately NOT set: the strip stays on screen with its
   * rows intact, and only the clicked package's own footer says it is working.
   * Blanking a table the operator is reading to add rows to it is a worse
   * answer than a two-second wait.
   */
  expandPackage(packageId: string) {
    if (!packageId || this.expandingPackageId) return;
    if (!this.expandedPackageIds.includes(packageId)) {
      this.expandedPackageIds = [...this.expandedPackageIds, packageId];
    }
    this.expandingPackageId = packageId;

    this.impactBriefService
      .getBrief(this.briefDomain, { expandedPackageIds: this.expandedPackageIds })
      .subscribe({
        next: (payload) => {
          if (payload) this.brief = payload;
          this.expandingPackageId = null;
        },
        error: () => {
          // Keep the capped brief on screen; the row simply stops saying it is
          // working. Losing the whole strip because the tail failed to load
          // would cost more than the thirteen rows are worth.
          this.expandingPackageId = null;
        }
      });
  }

  /**
   * Whether the packages section has something to say. An empty package list is
   * still worth rendering — it says the period produced no package that cleared
   * the bar, which is different from the brief failing to load.
   */
  get hasBrief(): boolean {
    return this.brief !== null;
  }

  /**
   * IB4 / DASHBOARD_HOME ladder 4. Everything below serves the states where
   * there is no crawl pair to show movement over — first run, nothing captured
   * yet, or a comparison the diff layer refused. Those used to be dead ends
   * ("come back after the next crawl"), which is wrong whenever the Issues queue
   * already has rows: the work is knowable from gap and opportunity alone, and
   * that is what `mode: 'issues-potential'` returns.
   */

  /**
   * Which panel the no-pair blocks show. One decision, pinned in
   * `utils/impact-brief-first-run.spec.ts`, so the three blocks below cannot
   * drift into disagreeing about what an empty brief means.
   */
  get firstRunPanel(): ImpactBriefFirstRunPanel {
    return impactBriefFirstRunPanel(this.brief, this.briefLoading);
  }

  /** Something to render. An empty package list is an answer, not a failure. */
  get hasPackages(): boolean {
    return this.firstRunPanel === 'packages';
  }

  /**
   * Rows in the full Issues queue behind the packages. Non-zero is precisely
   * the case IB4 exists for: work is waiting even though nothing moved.
   */
  get openQueueItems(): number {
    return this.brief?.totals.openQueueItems ?? 0;
  }

  /**
   * The sentence for an empty brief, from the same helper the packages
   * component uses inline. Shared so the dashboard's no-pair panel and the
   * component cannot describe the same `emptyState` in two different ways;
   * the call to action beside it is what differs, and that is IB4's part.
   */
  get emptyMessage(): string {
    return impactBriefEmptyMessage(this.brief?.emptyState ?? null);
  }

  /**
   * The domain to title a no-movers page with. Ladder 4 can reach this page
   * with no captured run at all, and then the movers payload — normally where
   * the header gets its name — is null. The brief resolved a domain to compose
   * against, so it can name one.
   */
  get briefDomain(): string | null {
    return this.brief?.domain?.trim() || null;
  }

  /**
   * True when the brief could not be read at all: a network failure, or the
   * production `ACTION_PLAN_V2_ENABLED` gate the endpoint sits behind (IB1
   * kept it there deliberately — the route reads the same synthesis as
   * `/action-plan/v2` and must not be a side door around the flag). Either way
   * the page falls back to the copy it had before packages existed rather than
   * showing an error for a section that is not the point of the screen.
   */
  get briefUnavailable(): boolean {
    return this.firstRunPanel === 'unavailable';
  }

  get hasPair(): boolean {
    return this.movers?.state === 'compared';
  }

  get isEmptyWorkspace(): boolean {
    return this.movers === null;
  }

  get isFirstRun(): boolean {
    return this.movers?.state === 'first-run';
  }

  /**
   * The period this page is about. Same helper Issues and URL Detail use, so
   * the three surfaces cannot drift into naming the same window differently.
   */
  period(): CrawlRunPeriodView | null {
    return buildCrawlRunPeriodView(this.movers);
  }

  /**
   * Always one of the three, never ''. See `.change-flat` in components.css:
   * an empty tone forces the host to paint its own default, and a host's rule
   * outranks the global tone under emulated encapsulation.
   */
  changeTone(change: number | null | undefined): string {
    if (change == null || change === 0) return 'change-flat';
    return change > 0 ? 'change-up' : 'change-down';
  }

  /**
   * The three site totals, in the order the strip reads them. A list rather
   * than three copies of the same markup: the template repeated one block per
   * metric, which is how the middle one ends up bound to the wrong field.
   *
   * The order is the payload's own — traffic, then keywords ranked, then
   * keywords in the top N — and it is deliberate: the third is a subset of the
   * second, and the second only means something after the first.
   */
  summaryMetrics(summary: CrawlRunSiteSummary): CrawlRunSiteMetricCard[] {
    return [summary.estimatedOrganicTraffic, summary.rankedKeywordCount, summary.keywordsInTopN];
  }

  issuesLink(): string[] {
    return ['/inventory'];
  }
}
